import React, {Fragment} from 'react';
import {List, ListItem, Stack} from '@mui/material';

import {ANSText, HapNdiLogo, ProductLogo} from 'presentation/components';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {translations} from 'presentation/translations-new';
import {stringfyDate} from 'presentation/utils';
import {DateFormatEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import FEATURE_FLAG_COMPONENTS from 'presentation/constants/featureFlagComponents';
import {ANSTextEnum} from 'presentation/components/ANSText/constants';
import {HealthCard} from '../HealthCard';
import {
	TemplateContainer,
	StyledCard,
	StyledCardContainer,
	StyledListSubheader,
	TemplateHeader,
	StyledListItemText,
} from './styles';
import {formatDateString} from '../../utils';
import {PrintTemplateProps} from './types';
import {HealthCardNotreDame} from '../HealthCardNotreDame';

const {
	beneficiaryInfo,
	planInfo,
	adherence,
	ansPlanRegister,
	birthday,
	cns,
	company,
	hiredCompany,
	name,
	partialCoverage,
	plan,
	cardCode,
	registration,
	civilName,
	socialName,
	accomodationType,
	network,
	cancellation,
	segmentation,
} = translations['pt-br'].privateFlow.healthCard.cardData;

export function PrintTemplate({data}: PrintTemplateProps) {
	const {
		cdUsuarioC,
		dtCpt,
		nomeEmpresa,
		nuCNS,
		nuMatriculaEmpresa,
		dtAdesaoC,
		nmPlano,
		nmOpeContratada,
		nmSocialUsuarioC,
		nmUsuarioC,
		dtNascimentoUsuarioC,
		flPlanoIntegrado,
		tipoAcomodacaoPlano,
		redeCarteirinha,
		registroAnsSemMascara,
		segmentacaoPlano,
	} = data;
	const {dataHostNameState, isClinipam, isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const {product} = dataHostNameState;
	const {isValid, contractCancellationDate} = useContractStatus();
	const {isTablet} = useMediaQueries();

	const customLogoPrintTemplateHeight = () => {
		if (isClinipam) return 53;
		if (isGndiSaoPaulo) return 50;
		return 32;
	};

	return (
		<Fragment>
			<TemplateHeader>
				{flPlanoIntegrado ? <HapNdiLogo /> : <ProductLogo customHeight={40} />}
			</TemplateHeader>
			<TemplateContainer>
				<StyledCardContainer>
					{isNotredameSaude ? (
						<HealthCardNotreDame key={data.nmPlano} data={data} hiddenCopyIcon />
					) : (
						<HealthCard key={data.nmPlano} data={data} hiddenCopyIcon />
					)}
				</StyledCardContainer>
				<StyledCard elevation={0}>
					<List
						sx={{flex: 1}}
						subheader={<StyledListSubheader>{planInfo}</StyledListSubheader>}>
						<ListItem disableGutters divider>
							<Stack direction="row" gap={8} flex={1}>
								<StyledListItemText
									primary={adherence}
									secondary={!dtAdesaoC ? '-' : dtAdesaoC}
								/>
								{isTablet ? null : (
									<StyledListItemText
										primary={partialCoverage}
										secondary={formatDateString(dtCpt)}
									/>
								)}
							</Stack>
						</ListItem>
						{isTablet ? (
							<ListItem disableGutters divider>
								<StyledListItemText
									primary={partialCoverage}
									secondary={formatDateString(dtCpt)}
								/>
							</ListItem>
						) : null}
						{!isValid ? (
							<ListItem disableGutters divider>
								<Stack direction="row" justifyContent="space-between" flex={1}>
									<StyledListItemText
										primary={cancellation}
										secondary={
											contractCancellationDate
												? stringfyDate(
														contractCancellationDate,
														DateFormatEnum.DATE_FORMAT_8,
													)
												: '-'
										}
									/>
								</Stack>
							</ListItem>
						) : null}
						<ListItem disableGutters divider>
							<StyledListItemText
								primary={company}
								secondary={!nomeEmpresa ? '-' : nomeEmpresa}
							/>
						</ListItem>
						<ListItem disableGutters divider>
							<StyledListItemText
								primary={plan}
								secondary={!nmPlano ? '-' : nmPlano}
							/>
						</ListItem>
						<ListItem disableGutters divider>
							<StyledListItemText
								primary={network}
								secondary={!redeCarteirinha ? '-' : redeCarteirinha}
							/>
						</ListItem>
						<ListItem disableGutters divider>
							<StyledListItemText
								primary={cardCode}
								secondary={!cdUsuarioC ? '-' : cdUsuarioC}
							/>
						</ListItem>
						<ListItem disableGutters divider>
							<StyledListItemText
								primary={accomodationType}
								secondary={!tipoAcomodacaoPlano ? '-' : tipoAcomodacaoPlano}
							/>
						</ListItem>
						<ListItem disableGutters divider>
							<StyledListItemText
								secondaryTypographyProps={{textTransform: 'uppercase'}}
								primary={hiredCompany}
								secondary={!nmOpeContratada ? '-' : nmOpeContratada}
							/>
						</ListItem>
						<ListItem
							disableGutters
							divider={FEATURE_FLAG_COMPONENTS.healthCard.segmentation}>
							<StyledListItemText
								primary={ansPlanRegister}
								secondary={!registroAnsSemMascara ? '-' : registroAnsSemMascara}
							/>
						</ListItem>
						{FEATURE_FLAG_COMPONENTS.healthCard.segmentation ? (
							<ListItem disableGutters>
								<StyledListItemText
									primary={segmentation}
									secondary={!segmentacaoPlano ? '-' : segmentacaoPlano}
								/>
							</ListItem>
						) : null}
					</List>
					<Stack flex={1}>
						<List
							sx={{flex: 1}}
							subheader={
								<StyledListSubheader>{beneficiaryInfo}</StyledListSubheader>
							}>
							{nmSocialUsuarioC ? (
								<ListItem disableGutters divider>
									<StyledListItemText
										secondaryTypographyProps={{textTransform: 'uppercase'}}
										primary={socialName}
										secondary={nmSocialUsuarioC}
									/>
								</ListItem>
							) : null}
							<ListItem disableGutters divider>
								<StyledListItemText
									secondaryTypographyProps={{textTransform: 'uppercase'}}
									primary={nmSocialUsuarioC ? civilName : name}
									secondary={!nmUsuarioC ? '-' : nmUsuarioC}
								/>
							</ListItem>
							<ListItem disableGutters divider>
								<Stack direction="row" justifyContent="space-between" flex={1}>
									<StyledListItemText
										primary={birthday}
										secondary={
											!dtNascimentoUsuarioC ? '-' : dtNascimentoUsuarioC
										}
									/>
									<StyledListItemText
										primary={registration}
										secondary={!nuMatriculaEmpresa ? '-' : nuMatriculaEmpresa}
									/>
								</Stack>
							</ListItem>
							<ListItem disableGutters>
								<StyledListItemText
									primary={cns}
									secondary={!nuCNS ? '-' : nuCNS}
								/>
							</ListItem>
						</List>
						<Stack alignItems="center" gap={flPlanoIntegrado ? 2 : 1} mt={3} mb={6}>
							{flPlanoIntegrado ? (
								<HapNdiLogo />
							) : (
								<ProductLogo customHeight={customLogoPrintTemplateHeight()} />
							)}
							<ANSText operator={flPlanoIntegrado ? ANSTextEnum.ALL : product} />
						</Stack>
					</Stack>
				</StyledCard>
			</TemplateContainer>
		</Fragment>
	);
}
